import React, { useCallback, useMemo } from 'react';
import { WEB_MAX_CONTENT_WIDTH } from '../../theme/webLayoutWidths';
import { ScrollView, StyleSheet, Text, View, Platform } from 'react-native';
import { GTFonts, useTheme } from '../../theme';
import { fs, s, v, iconSize } from '../../utils/libs';
import MaterialCommunityIcons from '@react-native-vector-icons/material-design-icons/static';
import ReportCard from '../../components/Reusables/ReportCard';
import { useNavigation } from '@react-navigation/native';
import type { NativeStackNavigationProp } from '@react-navigation/native-stack';
import type { RootStackParamList } from '../../navigation/AppStack';
import appAnalytics from '../../services/appAnalytics';
import { buildAppAnalyticsContext } from '../../services/appAnalyticsContext';
import type { ReportId } from '../../services/appAnalyticsTypes';

const BackOfficeReportsScreen: React.FC = () => {
  const { theme } = useTheme();
  const styles = useMemo(() => createStyles(theme), [theme]);
  const nav = useNavigation<NativeStackNavigationProp<RootStackParamList>>();

  const openReport = useCallback(
    (reportId: ReportId, title: string) => {
      appAnalytics.track('reports_report_opened', {
        ...buildAppAnalyticsContext(),
        report_id: reportId,
      });
      nav.navigate('BackOfficeReportPage', {
        reportId: reportId as any,
        title,
      });
    },
    [nav],
  );

  return (
    <ScrollView contentContainerStyle={styles.container}>
      <Text style={styles.subtitle}>
        Analyze sales, products, and employees
      </Text>

      <Text style={styles.section}>Sales Reports</Text>
      <View style={styles.grid2}>
        <ReportCard
          title="Sales Report"
          subtitle="View all sales transactions and filter by date"
          icon={
            <MaterialCommunityIcons
              name="chart-line"
              color={theme.colors.textInverse}
              size={iconSize(22)}
            />
          }
          color={theme.colors.primary}
          onPress={() => openReport('SALES_REPORT', 'Sales Report')}
        />
        <ReportCard
          title="Daily Opening and Closing Stocks"
          subtitle="Track daily stock opening and closing levels"
          icon={
            <MaterialCommunityIcons
              name="chart-timeline-variant"
              color={theme.colors.textInverse}
              size={iconSize(22)}
            />
          }
          color={theme.colors.warning}
          onPress={() =>
            openReport('DAILY_STOCKS', 'Daily Opening and Closing Stocks')
          }
        />
      </View>

      <View style={styles.grid2}>
        <ReportCard
          title="Monthly Sales Report"
          subtitle="Summarized daily sales totals for a date range"
          icon={
            <MaterialCommunityIcons
              name="calendar-month"
              color={theme.colors.textInverse}
              size={iconSize(22)}
            />
          }
          color={theme.colors.success}
          onPress={() =>
            openReport('MONTHLY_SALES_REPORT', 'Monthly Sales Report')
          }
        />
        <ReportCard
          title="Yearly Sales Report"
          subtitle="Summarized monthly transactions by year"
          icon={
            <MaterialCommunityIcons
              name="calendar"
              color={theme.colors.textInverse}
              size={iconSize(22)}
            />
          }
          color={theme.colors.info}
          onPress={() =>
            openReport('YEARLY_SALES_TOTALS', 'Yearly Sales Report')
          }
        />
      </View>

      <Text style={styles.section}>Products Reports</Text>
      <View style={styles.grid2}>
        <ReportCard
          title="Updated Products Report"
          subtitle="Track added and updated products and filter by date"
          icon={
            <MaterialCommunityIcons
              name="history"
              color={theme.colors.textInverse}
              size={iconSize(22)}
            />
          }
          color={theme.colors.info}
          onPress={() =>
            openReport('UPDATED_PRODUCTS', 'Updated Products Report')
          }
        />
        <ReportCard
          title="Best Selling Products"
          subtitle="Most sold products, filter by date"
          icon={
            <MaterialCommunityIcons
              name="star"
              color={theme.colors.textInverse}
              size={iconSize(22)}
            />
          }
          color={theme.colors.premium}
          onPress={() =>
            openReport('BEST_SELLING_PRODUCTS', 'Best Selling Products')
          }
        />
      </View>

      <Text style={styles.section}>Staff Reports</Text>
      <View style={styles.grid2}>
        <ReportCard
          title="All Staff Report"
          subtitle="View detailed employee information"
          icon={
            <MaterialCommunityIcons
              name="account-group"
              color={theme.colors.textInverse}
              size={iconSize(22)}
            />
          }
          color={theme.colors.warning}
          onPress={() => openReport('EMPLOYEES', 'All Staff Report')}
        />
      </View>
    </ScrollView>
  );
};

export default BackOfficeReportsScreen;

const createStyles = (theme: any) =>
  StyleSheet.create({
    container: {
      padding: s(10),
      paddingBottom: s(20),
      backgroundColor: theme.colors.surface,
      gap: v(6),
      ...(Platform.OS === 'web' && {
        maxWidth: WEB_MAX_CONTENT_WIDTH,
        alignSelf: 'center',
        width: '100%',
        padding: s(24),
      }),
    },
    title: {
      fontSize: fs(20),
      lineHeight: fs(24),
      fontFamily: GTFonts.semibold,
      color: theme.colors.text,
      marginBottom: v(6),
    },
    subtitle: {
      fontSize: fs(14),
      lineHeight: fs(16),
      color: theme.colors.textSecondary,
      fontFamily: GTFonts.regular,
      marginBottom: v(10),
    },
    section: {
      fontSize: fs(16),
      lineHeight: fs(20),
      fontFamily: GTFonts.bold,
      color: theme.colors.text,
      marginTop: v(2),
      marginBottom: v(6),
    },
    grid2: {
      flexDirection: 'row',
      justifyContent: 'space-between',
      flexWrap: 'wrap',
      // marginBottom: v(8),
    },
  });
